/* eslint-disable react/no-unescaped-entities */
'use client';

import React from 'react';

import { Button, Card, Heading } from '@ui';
import MarkupSnippet from '../markup-snippet/MarkupSnippet';
import { IconReact } from '@icons';
import { withOptionalId } from '@lib/withOptionalId';
import { Section, Container, SubSectionHeader } from '@layout';

interface ButtonsProps {
  sectionID?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  isSpanish?: boolean;
}

const Buttons: React.FC<ButtonsProps> = ({
  isSpanish = false,
  sectionID = '',
  classes = '',
  containerId,
  containerClasses,
}) => {
  const t = (en: string, es: string) => (isSpanish ? es : en);
  const buttonHtml = (className: string, label: string) =>
    `<button class="btn ${className}" type="button">\n  <span class="label">${label}</span>\n</button>`;
  const snippetLabels = {
    label: t('HTML', 'HTML'),
    copyLabel: t('Copy', 'Copiar'),
    copiedLabel: t('Copied', 'Copiado'),
  };

  return (
    <Section {...withOptionalId(sectionID)} className={classes}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <Heading as='h3'>{t('Buttons', 'Botones')}</Heading>
        <p className='sub full'>
          {t(
            'Hierarchy is intentional: one primary action per region, secondary for supporting work, tertiary for lighter emphasis. Outline and ghost are style variants—not extra tiers. Destructive and warning styles are for risky or system-level states—not decoration.',
            'La jerarquia es intencional: una accion principal por region, secundaria de apoyo, terciaria para menor enfasis. Contorno y transparente son variantes de estilo—no niveles extra. Advertencia y peligro son para riesgo o estados del sistema, no decoracion.'
          )}
        </p>

        <div className='buttons-demo'>
          <div className='pair'>
            <div className='subsection'>
              <SubSectionHeader
                as='h4'
                title={t('Primary action', 'Accion Principal')}
                description={t(
                  'Primary: the main CTA per block—the hierarchy anchor.',
                  'Principal: la CTA principal por bloque—el ancla de jerarquia.'
                )}
              />
              <Card className='surface-1'>
                <div className='row btn-row gap'>
                  <Button label={t('Continue', 'Continuar')} className='btn-primary' />
                </div>
                <MarkupSnippet
                  {...snippetLabels}
                  code={buttonHtml('btn-primary', t('Continue', 'Continuar'))}
                />
              </Card>
            </div>

            <div className='subsection'>
              <SubSectionHeader
                as='h4'
                title={t('Secondary & tertiary actions', 'Acciones secundarias y terciarias')}
                description={t(
                  'Secondary & tertiary actions: alternatives that support the primary without competing with it.',
                  'Secundaria y terciaria de apoyo: alternativas que apoyan a la principal sin competir con ella.'
                )}
              />
              <Card className='surface-1'>
                <div className='row btn-row gap'>
                  <Button label={t('Secondary', 'Secundario')} className='btn-secondary' />
                  <Button label={t('Tertiary', 'Terciario')} className='btn-tertiary' />
                </div>
                <MarkupSnippet
                  {...snippetLabels}
                  code={[
                    buttonHtml('btn-secondary', t('Secondary', 'Secundario')),
                    buttonHtml('btn-tertiary', t('Tertiary', 'Terciario')),
                  ].join('\n\n')}
                />
              </Card>
            </div>
          </div>

          <div className='pair'>
            <div className='subsection'>
              <SubSectionHeader
                as='h4'
                title={t('Variants & styles', 'Variantes y estilos')}
                description={t(
                  'Outline, ghost, and icon placement—visual treatments, not hierarchy tiers.',
                  'Contorno, transparente e iconos—tratamientos visuales, no niveles de jerarquia.'
                )}
              />
              <Card className='surface-1'>
                <p className='row-label small' style={{ color: 'var(--muted)', marginTop: 0 }}>
                  {t('Outline', 'Contorno')}
                </p>
                <div className='row btn-row gap'>
                  <Button label={t('Outline', 'Contorno')} className='btn-outline' />
                  <Button
                    label={t('Outline primary', 'Contorno principal')}
                    className='btn-outline-primary'
                  />
                  <Button
                    label={t('Outline secondary', 'Contorno secundario')}
                    className='btn-outline-secondary'
                  />
                  <Button
                    label={t('Outline tertiary', 'Contorno terciario')}
                    className='btn-outline-tertiary'
                  />
                </div>
                <div className='nested on-dark'>
                  <p className='nested-label'>{t('Outline white', 'Contorno blanco')}</p>
                  <p className='nested-hint'>
                    {t(
                      'White stroke for dark or colored surfaces, such as a hero.',
                      'Trazo blanco para superficies oscuras o de color, como un hero.'
                    )}
                  </p>
                  <div className='row btn-row gap'>
                    <Button
                      label={t('Outline white', 'Contorno blanco')}
                      className='btn-outline-white'
                    />
                  </div>
                  <MarkupSnippet
                    {...snippetLabels}
                    code={buttonHtml('btn-outline-white', t('Outline white', 'Contorno blanco'))}
                  />
                </div>
                <div className='nested'>
                  <p className='nested-label'>{t('Ghost', 'Transparente')}</p>
                  <p className='nested-hint'>
                    {t(
                      'Quiet actions on bold or busy backgrounds—same style tier as outline, different surface.',
                      'Acciones discretas sobre fondos intensos—mismo tipo de variante que el contorno, otra superficie.'
                    )}
                  </p>
                  <div className='row btn-row gap'>
                    <Button label={t('Ghost', 'Transparente')} className='btn-ghost' />
                  </div>
                </div>
                <div className='nested'>
                  <p className='nested-label'>{t('Icon buttons', 'Botones con icono')}</p>
                  <p className='nested-hint'>
                    {t(
                      'Leading, trailing, and icon-only patterns.',
                      'Icono al inicio, al final o solo icono.'
                    )}
                  </p>
                  <div className='row btn-row gap'>
                    <Button
                      label={t('Leading icon', 'Icono inicial')}
                      className='btn-secondary'
                      icon='✓'
                      iconPosition='left'
                    />
                    <Button
                      label={t('Trailing icon', 'Icono final')}
                      className='btn-secondary'
                      icon='→'
                      iconPosition='right'
                    />
                    <Button
                      label=''
                      className='btn-ghost btn-icon'
                      icon={<IconReact />}
                      aria-label={t('Settings', 'Configuracion')}
                    />
                  </div>
                </div>
                <MarkupSnippet
                  {...snippetLabels}
                  code={[
                    buttonHtml('btn-outline', t('Outline', 'Contorno')),
                    buttonHtml('btn-outline-primary', t('Outline primary', 'Contorno principal')),
                    buttonHtml('btn-outline-secondary', t('Outline secondary', 'Contorno secundario')),
                    buttonHtml('btn-outline-tertiary', t('Outline tertiary', 'Contorno terciario')),
                    buttonHtml('btn-ghost', t('Ghost', 'Transparente')),
                    `<button class="btn btn-secondary" type="button">\n  <span class="iconify" aria-hidden="true">✓</span>\n  <span class="label">${t('Leading icon', 'Icono inicial')}</span>\n</button>`,
                  ].join('\n\n')}
                />
              </Card>
            </div>

            <div className='subsection'>
              <SubSectionHeader
                as='h4'
                title={t('States', 'Estados')}
                description={t(
                  'Disabled, loading, and link-styled actions.',
                  'Deshabilitado, carga y estilo enlace.'
                )}
              />
              <Card className='surface-1'>
                <div className='row btn-row gap'>
                  <Button label={t('Disabled', 'Deshabilitado')} className='btn-primary' disabled />
                  <Button
                    label={t('Loading', 'Cargando')}
                    className='btn-primary is-loading'
                    aria-busy='true'
                  />
                  <Button
                    label={t('Click to load', 'Haz clic para cargar')}
                    className='btn-primary'
                    data-demo-loading
                  />
                  <Button
                    label={t('As link', 'Como enlace')}
                    className='btn-link'
                    as='a'
                    href='#buttons'
                    role='button'
                  />
                </div>
                <MarkupSnippet
                  {...snippetLabels}
                  code={[
                    `<button class="btn btn-primary" type="button" disabled>\n  <span class="label">${t('Disabled', 'Deshabilitado')}</span>\n</button>`,
                    `<button class="btn btn-primary is-loading" type="button" aria-busy="true">\n  <span class="label">${t('Loading', 'Cargando')}</span>\n</button>`,
                    `<a class="btn btn-link" href="#buttons" role="button">${t('As link', 'Como enlace')}</a>`,
                  ].join('\n\n')}
                />
              </Card>
            </div>
          </div>

          <div className='subsection'>
            <SubSectionHeader
              as='h4'
              title={t('Destructive & system feedback', 'Destructivas y feedback del sistema')}
              description={t(
                'Use for irreversible actions, alerts, and validation—not for everyday navigation.',
                'Para acciones irreversibles, alertas y validacion—no para navegacion cotidiana.'
              )}
            />
            <Card className='surface-1'>
              <div className='row btn-row gap'>
                <Button label={t('Warning', 'Advertencia')} className='btn-warning' />
                <Button label={t('Danger', 'Peligro')} className='btn-danger' />
              </div>
              <div className='sep' />
              <div className='row btn-row gap'>
                <Button
                  label={t('Outline warning', 'Contorno advertencia')}
                  className='btn-outline-warning'
                />
                <Button
                  label={t('Outline danger', 'Contorno peligro')}
                  className='btn-outline-danger'
                />
              </div>
              <MarkupSnippet
                {...snippetLabels}
                code={[
                  buttonHtml('btn-warning', t('Warning', 'Advertencia')),
                  buttonHtml('btn-danger', t('Danger', 'Peligro')),
                  buttonHtml('btn-outline-warning', t('Outline warning', 'Contorno advertencia')),
                  buttonHtml('btn-outline-danger', t('Outline danger', 'Contorno peligro')),
                ].join('\n\n')}
              />
            </Card>
          </div>

          <div className='pair'>
            <div className='subsection'>
              <SubSectionHeader as='h4' title={t('Sizes', 'Tamanos')} />
              <Card className='surface-1'>
                <div className='row btn-row gap'>
                  <Button label={t('Small', 'Pequeno')} className='btn-primary btn-sm' />
                  <Button label={t('Default', 'Predeterminado')} className='btn-primary' />
                  <Button label={t('Large', 'Grande')} className='btn-primary btn-lg' />
                </div>
                <MarkupSnippet
                  {...snippetLabels}
                  code={[
                    buttonHtml('btn-primary btn-sm', t('Small', 'Pequeno')),
                    buttonHtml('btn-primary', t('Default', 'Predeterminado')),
                    buttonHtml('btn-primary btn-lg', t('Large', 'Grande')),
                  ].join('\n\n')}
                />
              </Card>
            </div>

            <div className='subsection'>
              <SubSectionHeader as='h4' title={t('Layout', 'Diseno')} />
              <Card className='surface-1'>
                <div className='row btn-row gap' style={{ alignItems: 'stretch' }}>
                  <div style={{ flex: 1 }}>
                    <Button
                      label={t('Full width', 'Ancho completo')}
                      className='btn-primary btn-block'
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className='btn-group'>
                    <Button label={t('Left', 'Izquierda')} className='btn-ghost' />
                    <Button label={t('Middle', 'Medio')} className='btn-ghost' />
                    <Button label={t('Right', 'Derecha')} className='btn-ghost' />
                  </div>
                </div>
                <MarkupSnippet
                  {...snippetLabels}
                  code={`<button class="btn btn-primary btn-block" type="button">\n  <span class="label">${t('Full width', 'Ancho completo')}</span>\n</button>`}
                />
              </Card>
            </div>
          </div>

          <div className='subsection'>
            <SubSectionHeader
              as='h4'
              title={t('Example: dialog actions', 'Ejemplo: acciones de dialogo')}
              description={t(
                'Typical footer: dismiss, commit, and destructive control aligned together.',
                'Pie tipico: cerrar, confirmar y control destructivo alineados.'
              )}
            />
            <Card className='dialog-footer'>
              <div
                className='row btn-row gap'
                style={{ justifyContent: 'flex-end', flexWrap: 'wrap' }}
              >
                <Button label={t('Cancel', 'Cancelar')} className='btn-ghost' />
                <Button label={t('Save', 'Guardar')} className='btn-primary' />
                <Button label={t('Delete', 'Eliminar')} className='btn-danger' />
              </div>
              <MarkupSnippet
                {...snippetLabels}
                code={[
                  buttonHtml('btn-ghost', t('Cancel', 'Cancelar')),
                  buttonHtml('btn-primary', t('Save', 'Guardar')),
                  buttonHtml('btn-danger', t('Delete', 'Eliminar')),
                ].join('\n\n')}
              />
            </Card>
          </div>
        </div>
      </Container>
    </Section>
  );
};

export default Buttons;
